import type { RoleScopeKinds } from "@/api/access/access.types";
import { buildScopeBoxRows, reachOption, REACH_OPTIONS } from "./roleScope";
import type { Reach } from "./roleScope";
import type {
  Role,
  RoleDataById,
  RoleDeactivateImpactRead,
  RoleHoldersResponse,
} from "./roles-permissions.queries.types";
import { LEVEL_BADGE_LABEL } from "./rolesDirectory.types";
import type {
  RoleDeactivateImpact,
  RoleHolderLosingAccess,
  RoleHoldersPreview,
  RoleLevel,
  RoleListItemWithDirectory,
  RoleWithDirectory,
  RolesDirectoryClient,
} from "./rolesDirectory.types";

const NAMED_HOLDERS = 3;

/** `roles.reach` from the role read; a value this build does not know reads as none set. */
export const reachOf = (reach: unknown): Reach | null =>
  REACH_OPTIONS.find((option) => option.value === reach)?.value ?? null;

export const isRoleLevel = (value: unknown): value is RoleLevel =>
  typeof value === "string" && Object.hasOwn(LEVEL_BADGE_LABEL, value);

/** `roles.scope` from the role read; a value this build does not know reads as unknown. */
export const roleLevelOf = (role: { level?: unknown }): RoleLevel | null =>
  isRoleLevel(role.level) ? role.level : null;

/**
 * The boxes a holder fills in, named as the Scope tab names them. A response without `scopeKinds`
 * is unknown (`null`, the list shows "—"); an empty object is a role that uses no boxes.
 */
export const scopeBoxesOf = (scopeKinds: RoleScopeKinds | null | undefined): string[] | null =>
  scopeKinds && typeof scopeKinds === "object"
    ? buildScopeBoxRows(scopeKinds).map((row) => row.label)
    : null;

const UNNAMED_PERSON = "Unnamed person";
const NO_COMPANY = "—";

export const losingAllAccessOf = (impact: RoleDeactivateImpactRead): RoleHolderLosingAccess[] =>
  (impact.losingAllAccess ?? []).map((person) => ({
    name: person.name?.trim() || UNNAMED_PERSON,
    entity: person.tenantName?.trim() || NO_COMPANY,
  }));

/**
 * `holderCount` counts holder rows: one per company copy, ended temporary ones included. So the
 * count is the read's distinct people, else the preview's, and is unknown when neither is complete.
 * The deactivate-impact read's own `holders` counts one copy's rows, so it does not feed the count;
 * it says who would be left with no role, and is unknown (`null`) until it answers.
 */
export const deactivateImpactOf = (
  role: Role,
  holders: RoleHoldersPreview | null,
  impact: RoleDeactivateImpactRead | null,
): RoleDeactivateImpact => ({
  holders:
    typeof role.holderPeopleCount === "number"
      ? role.holderPeopleCount
      : role.holderCount === 0
        ? 0
        : (holders?.people ?? null),
  namedHolders: holders?.names ?? [],
  losingAllAccess: impact ? losingAllAccessOf(impact) : null,
});

/**
 * The holders route lists one row per person per company copy, so a person in two companies
 * appears twice; an ended temporary assignment no longer holds the role.
 */
export const holdersPreviewOf = ({ data, total }: RoleHoldersResponse): RoleHoldersPreview => {
  const people = new Set<string>();
  const namesByPerson = new Map<string, string>();
  for (const holder of data) {
    if (holder.ended) continue;
    people.add(holder.userId);
    const name = holder.name?.trim();
    if (name && !namesByPerson.has(holder.userId)) namesByPerson.set(holder.userId, name);
  }
  return {
    people: data.length >= total ? people.size : null,
    names: [...namesByPerson.values()].slice(0, NAMED_HOLDERS),
  };
};

export const liveRolesDirectoryClient: RolesDirectoryClient = {
  decorate: (role: RoleDataById): RoleWithDirectory => ({
    ...role,
    level: roleLevelOf(role),
    department: role.department ?? null,
    reach: reachOf(role.reach),
    deactivatedAt: role.deactivatedAt ?? null,
    deactivatedBy: role.deactivatedBy?.name ?? null,
  }),
  decorateListRole: (role: Role): RoleListItemWithDirectory => ({
    ...role,
    level: roleLevelOf(role),
    department: role.department ?? null,
    peopleCount: role.holderPeopleCount ?? role.holderCount ?? null,
    scopeBoxes: scopeBoxesOf(role.scopeKinds),
    reach: reachOption(reachOf(role.reach))?.name ?? null,
  }),
  getLastSavedBy: (role) => role.updatedBy?.name ?? null,
  getDeactivateImpact: deactivateImpactOf,
};
